import React from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { IReduxState } from 'interfaces/IReduxState'
import { useDispatch, useSelector } from 'react-redux'
import { VALUE_IN, VALUE_OUT } from 'enums/stockCard'
import MuiAlert from '@mui/material/Alert'

import API from '@aws-amplify/api'
import { IBatch, IPremix } from 'interfaces/IPremix'
import {
  IPremixRequest,
  PremixRequest,
  PremixRequestSourceOrDestination,
} from 'interfaces/IPremixRequest'
import LoadPremixAutocomplete from 'shared/autocomplete/loadPremixAutocomplete'
import LoadBatchAutocomplete from 'shared/autocomplete/loadBatchAutocomplete'
import { formatStockQuantity } from 'components/inventory/helpers'

export default function PremixRequestForm(props: {}) {
  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const [premixRequest, setPremixRequest] = React.useState<IPremixRequest>(
    new PremixRequest({})
  )
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [batches, setBatches] = React.useState<IBatch[]>([])
  const [selectedBatchNumber, setSelectedBatchNumber] =
    React.useState<string>('')
  const [requestedQty, setRequestedQty] = React.useState<string>('')
  const [premixAutocompleteKey, setPremixAutocompleteKey] =
    React.useState<number>(0)

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }
  const getMissingFields = (req: IPremixRequest): string[] => {
    const missing: string[] = []
    if (!req.inOutDirection) missing.push('In/Out')
    if (!req.sourceOrDestination) missing.push('Dari/Tujuan')
    // Must have a batch selected and qty > 0
    if (!selectedBatchNumber) missing.push('Bets')
    if (Number(requestedQty || 0) <= 0) missing.push('Quantity')

    // Notes required for some flows
    const requiresNotes = [
      PremixRequestSourceOrDestination.rnd,
      PremixRequestSourceOrDestination.inNoCost,
      PremixRequestSourceOrDestination.outNoCost,
      PremixRequestSourceOrDestination.stokQc,
      PremixRequestSourceOrDestination.adjustment,
      PremixRequestSourceOrDestination.adjustmentDamaged,
    ].includes((req as any).sourceOrDestination)
    if (requiresNotes && !req.notes) missing.push('Notes')

    return missing
  }

  const getValidationErrors = () => {
    const errors: string[] = []
    const missing = getMissingFields(premixRequest)
    if (missing.length > 0) {
      errors.push(`Lengkapi data berikut: ${missing.join(', ')}`)
    }
    return errors
  }

  const isAllFieldsComplete = (premixRequest: IPremixRequest) => {
    return getValidationErrors().length === 0
  }


  const handlePremixChange = (premix?: IPremix | null) => {
    console.log(premix)
    if (!premix || !premix.premixId) {
      setPremixRequest((prev) => ({
        ...prev,
        premixId: '',
      }))
      setBatches([])
      return
    }
    // Save selected premixId into state
    setPremixRequest((prev) => ({
      ...prev,
      premixId: premix.premixId,
    }))
    setSelectedBatchNumber('')
    setRequestedQty('')
  }

  const savePremixRequest = () => {
    setIsLoading(true)

    API.post('products', `/premix/premixRequest`, {
      body: {
        ...premixRequest,
        batches: batches
          .filter((batch) => batch.batchNumber === selectedBatchNumber)
          .map((batch) => ({ ...batch, qtyRequested: parseFloat(requestedQty) })),
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil membuat request ruahan.')
        setIsSuccessSnackbarOpen(true)
        // reset all local state to defaults
        setPremixRequest(new PremixRequest())
        setBatches([])
        setSelectedBatchNumber('')
        setRequestedQty('')
        // remount LoadPremixAutocomplete to clear its internal state
        setPremixAutocompleteKey((k) => k + 1)
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage('Gagal membuat request ruahan.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handlePremixRequestFieldChange = (value: any, key: string): void => {
    setPremixRequest((premixRequestData) => {
      const next = {
        ...premixRequestData,
        [key]: value,
      }

      // If switching to OUT, reset currently selected batch
      if (key === 'inOutDirection') {
        if (value === VALUE_OUT) {
          setSelectedBatchNumber('')
          setRequestedQty('')
        }
      }

      return next
    })
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Request Ruahan Form' })
  }, [dispatch])

  return (
    <React.Fragment>
      <Paper>
        <React.Fragment>
          <List>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      In/Out
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={premixRequest.inOutDirection}
                  onChange={(e) =>
                    handlePremixRequestFieldChange(
                      e.target.value,
                      'inOutDirection'
                    )
                  }
                >
                  <FormControlLabel
                    value={VALUE_IN}
                    control={<Radio color="primary" />}
                    label={'Masuk ke Gudang Ruahan'}
                  />
                  <FormControlLabel
                    value={VALUE_OUT}
                    control={<Radio color="primary" />}
                    label={'Keluar dari Gudang Ruahan'}
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      Dari/Tujuan
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={premixRequest.sourceOrDestination}
                  onChange={(e) =>
                    handlePremixRequestFieldChange(
                      e.target.value,
                      'sourceOrDestination'
                    )
                  }
                >
                  {premixRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.adjustment}
                      control={<Radio color="primary" />}
                      label={'Adjustment'}
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.rnd}
                      control={<Radio color="primary" />}
                      label={'RnD (Wajib tulis nomor request RnD di notes)'}
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.production}
                      control={<Radio color="primary" />}
                      label={'Produksi'}
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.stokQc}
                      control={<Radio color="primary" />}
                      label={'Stok QC'}
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.inNoCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang Masuk Tanpa Biaya (Wajib tulis notes dari siapa)'
                      }
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.rnd}
                      control={<Radio color="primary" />}
                      label={'RnD'}
                    />
                  )}

                  {premixRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.adjustment}
                      control={<Radio color="primary" />}
                      label={'Adjustment'}
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PremixRequestSourceOrDestination.outNoCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang Dikeluarkan Tanpa Biaya (Wajib tulis notes untuk siapa)'
                      }
                    />
                  )}
                  {premixRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={
                        PremixRequestSourceOrDestination.productionAdditionalRequest
                      }
                      control={<Radio color="primary" />}
                      label={`Produksi - Request Tambahan (wajib tulis nomor BIKIN STJ request di notes)`}
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem>
              <LoadPremixAutocomplete
                key={premixAutocompleteKey}
                onChange={handlePremixChange}
              />
            </ListItem>
            <ListItem>
              <LoadBatchAutocomplete
                premixId={premixRequest.premixId}
                onlyAvailable={premixRequest.inOutDirection === VALUE_OUT}
                name={selectedBatchNumber}
                onChange={(batch) => {
                  setSelectedBatchNumber(batch.batchNumber)
                }}
                getMatchedData={setBatches}
                loadAll
                disabled={!premixRequest.premixId}
              />
            </ListItem>
            {selectedBatchNumber && (
              <ListItem>
                <TextField
                  autoComplete="off"
                  variant="outlined"
                  value={requestedQty}
                  label={
                    premixRequest.inOutDirection === VALUE_IN
                      ? 'Qty mau dimasukan'
                      : 'Qty mau dikeluarkan'
                  }
                  fullWidth
                  onChange={(e) => setRequestedQty(e.target.value)}
                  placeholder={`Sisa: ${formatStockQuantity(batches.find((b) => b.batchNumber === selectedBatchNumber)?.currentQuantity ?? 0)}`}
                />
              </ListItem>
            )}

            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={premixRequest.notes}
                fullWidth
                label={'Notes Peminta'}
                error={getMissingFields(premixRequest).includes('Notes')}
                helperText={
                  getMissingFields(premixRequest).includes('Notes')
                    ? 'Wajib diisi untuk opsi tertentu'
                    : ''
                }
                onChange={(e) =>
                  handlePremixRequestFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem>
              <Typography>Request Dibuat Oleh: {user.username}</Typography>
            </ListItem>
          </List>
          {/* Missing fields helper */}
          {getMissingFields(premixRequest).length > 0 && (
            <Box mt={2}>
              <MuiAlert severity="warning" elevation={0} variant="outlined">
                Lengkapi data berikut sebelum menyimpan:{' '}
                {getMissingFields(premixRequest).join(', ')}
              </MuiAlert>
            </Box>
          )}
          <Box
            p={2}
            mt={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              disabled={!isAllFieldsComplete(premixRequest) || isLoading}
              onClick={savePremixRequest}
            >
              Save
            </Button>
            {!isAllFieldsComplete(premixRequest) && (
              <Box mt={1}>
                {getValidationErrors().map((err) => (
                  <Typography
                    key={err}
                    variant="caption"
                    color="error"
                    display="block"
                    align="right"
                  >
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </React.Fragment>
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
